/**
 * Timestamp utilities for standard fields
 *
 * Every table has standard audit fields:
 * - Regular tables:    created_at, created_by, updated_at, updated_by, deleted_at, deleted_by
 * - Junction tables:   created_at, created_by
 * - Immutable tables:  created_at, created_by, deleted_at, deleted_by
 * - Settings tables:   created_at, created_by, updated_at, updated_by
 */

/**
 * Generate timestamps for INSERT (new record)
 *
 * @example
 * ```ts
 * await db.insert(vehicles).values({
 *   id: generateUlid(),
 *   name: "Toyota Vios",
 *   ...createTimestamps(auth.userId),
 * });
 * ```
 */
export function createTimestamps(userId?: string | null) {
  const now = new Date().toISOString()
  return {
    createdAt: now,
    createdBy: userId ?? null,
    updatedAt: now,
    updatedBy: userId ?? null,
  }
}

/**
 * Generate timestamps for INSERT on junction/immutable tables (no updated fields)
 *
 * @example
 * ```ts
 * await db.insert(userRoles).values({
 *   id: generateUlid(),
 *   userId: "...",
 *   roleId: "...",
 *   ...createOnlyTimestamps(auth.userId),
 * });
 * ```
 */
export function createOnlyTimestamps(userId?: string | null) {
  return {
    createdAt: new Date().toISOString(),
    createdBy: userId ?? null,
  }
}

/**
 * Generate timestamps for UPDATE
 *
 * @example
 * ```ts
 * await db.update(vehicles)
 *   .set({ name: "Honda City", ...updateTimestamps(auth.userId) })
 *   .where(eq(vehicles.id, id));
 * ```
 */
export function updateTimestamps(userId?: string | null) {
  return {
    updatedAt: new Date().toISOString(),
    updatedBy: userId ?? null,
  }
}

/**
 * Generate timestamps for soft DELETE
 *
 * @example
 * ```ts
 * await db.update(vehicles)
 *   .set(deleteTimestamps(auth.userId))
 *   .where(eq(vehicles.id, id));
 * ```
 */
export function deleteTimestamps(userId?: string | null) {
  return {
    deletedAt: new Date().toISOString(),
    deletedBy: userId ?? null,
  }
}

/**
 * Generate timestamps for RESTORE (undo soft delete)
 *
 * @example
 * ```ts
 * await db.update(vehicles)
 *   .set({ ...restoreTimestamps(), ...updateTimestamps(auth.userId) })
 *   .where(eq(vehicles.id, id));
 * ```
 */
export function restoreTimestamps() {
  return {
    deletedAt: null,
    deletedBy: null,
  }
}
